import type { Grants } from '@emr/shared';
import { createContext, useContext, useMemo } from 'react';

import type { Session } from '../api/auth.ts';

/** The session query's cache key. A plain string array, matching the house convention. */
export const SESSION_QUERY_KEY = ['session'] as const;

export const SessionContext = createContext<Session | null>(null);

/**
 * The signed-in session.
 *
 * Throws outside `<RequireAuth>` rather than returning `null`. A component reading a nullable
 * session would have to branch on "not signed in" on every page that is only reachable when signed
 * in — and the branch nobody can trigger is the branch nobody keeps correct.
 */
export function useSession(): Session {
  const session = useContext(SessionContext);
  if (session === null) {
    throw new Error('useSession must be used inside <RequireAuth>');
  }
  return session;
}

/**
 * The session as `@emr/shared`'s authorization functions want it.
 *
 * Built here, once, so no component assembles a `Grants` of its own. `docs/roadmap.md` §3.1 and
 * `claude-kit/rules/invariants.md` both make the same point about the server side — one decision,
 * one place — and a UI that constructs the input to that decision in five components has the same
 * drift problem one level up.
 *
 * Both fields come from the server's session response and never from a form, a URL, or local
 * state: "server-authoritative values never come from the request", and its client-side corollary
 * is that the client must not invent them either.
 */
export function useGrants(): Grants {
  const session = useSession();
  return useMemo(
    () => ({ role: session.role, isPlatformOperator: session.isPlatformOperator }),
    [session.role, session.isPlatformOperator],
  );
}
